// pages/public/Programs.tsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { programService, Program } from '../../firebase/programFirestoreService';
import { useAuth } from '../../contexts/AuthContext'; 
import RequireLoginModal from '../../components/RequireLoginModal';
import { cache } from '../../utils/cacheManager';
import { 
  Calendar, 
  MapPin, 
  Users, 
  ArrowRight, 
  Search, 
  Filter,
  Clock,
  CheckCircle,
  CalendarDays,
  Award,
  Target,
  BarChart
} from 'lucide-react';
import { motion } from 'framer-motion';

const Programs: React.FC = () => {
  const { currentUser, isAuthenticated } = useAuth(); 
  const navigate = useNavigate();
  const [programs, setPrograms] = useState<Program[]>([]);
  const [filteredPrograms, setFilteredPrograms] = useState<Program[]>([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState<{id: string, title: string} | null>(null);
  const [now] = useState(new Date());

  useEffect(() => {
    loadPrograms();
  }, []);

  useEffect(() => {
    filterPrograms();
  }, [programs, categoryFilter, statusFilter, searchTerm]);

  const loadPrograms = async () => {
    try {
      const data = await cache.get<Program[]>('programs.published', () => programService.getPublishedPrograms(), 60 * 60 * 1000);
      setPrograms(data || []);
    } catch (error) {
      console.error('Error loading programs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterClick = (programId?: string, programTitle?: string) => {
    if (!programId) {
      alert('Unable to register: missing program id.');
      return;
    }

    if (!isAuthenticated) {
      setSelectedProgram({ id: programId, title: programTitle ?? 'Program' });
      setShowLoginModal(true);
      return;
    }
    
    navigate(`/programs/${programId}/register`);
  };

  // Helper function to convert any date format to Date object
  const parseDate = (date: any): Date | null => {
    if (!date) return null;
    
    try {
      if (date instanceof Date) return date;
      if (date.toDate && typeof date.toDate === 'function') return date.toDate();
      if (typeof date === 'string' || typeof date === 'number') return new Date(date);
      return null;
    } catch (error) {
      console.error('Error parsing date:', error, date);
      return null;
    }
  };

const getProgramStatus = (program: Program): {
  status: 'upcoming' | 'ongoing' | 'completed' | 'cancelled' | 'published';
  label: string;
  color: string;
  bgColor: string;
} => {
  const startDate = parseDate(program.startDate);
  const endDate = parseDate(program.endDate);
  const programStatus = program.status?.toLowerCase() || 'published';

  // Manual statuses first
  if (programStatus === 'cancelled') {
    return {
      status: 'cancelled',
      label: 'Cancelled',
      color: 'text-red-700',
      bgColor: 'bg-red-100'
    };
  }

  if (programStatus === 'completed') {
    return {
      status: 'completed',
      label: 'Completed',
      color: 'text-gray-700',
      bgColor: 'bg-gray-100'
    };
  }

  // Calculate based on dates
  if (!startDate) {
    return {
      status: 'published',
      label: 'Published',
      color: 'text-blue-700',
      bgColor: 'bg-blue-100'
    };
  }

  if (now < startDate) {
    return {
      status: 'upcoming',
      label: 'Upcoming',
      color: 'text-purple-700',
      bgColor: 'bg-purple-100'
    };
  }

  if (endDate && now > endDate) {
    return {
      status: 'completed',
      label: 'Completed',
      color: 'text-gray-700',
      bgColor: 'bg-gray-100'
    };
  }

  // Currently running
  if ((startDate && !endDate && now >= startDate) || 
      (startDate && endDate && now >= startDate && now <= endDate)) {
    return {
      status: 'ongoing',
      label: 'Ongoing',
      color: 'text-green-700',
      bgColor: 'bg-green-100'
    };
  }

  return {
    status: 'published',
    label: 'Published',
    color: 'text-blue-700',
    bgColor: 'bg-blue-100'
  };
};


  // Calculate days until/until completion
  const getDateInfo = (program: Program): {
    daysUntil: number | null;
    daysTotal: number | null;
    percentageComplete: number | null;
  } => {
    const startDate = parseDate(program.startDate);
    const endDate = parseDate(program.endDate);
    
    if (!startDate) return { daysUntil: null, daysTotal: null, percentageComplete: null };

    const daysUntil = Math.ceil((startDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    
    let daysTotal = null;
    let percentageComplete = null;

    if (endDate) {
      daysTotal = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
      
      if (now >= startDate && now <= endDate) {
        const elapsedDays = Math.ceil((now.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
        percentageComplete = Math.min(Math.round((elapsedDays / daysTotal) * 100), 100);
      } else if (now > endDate) {
        percentageComplete = 100;
      }
    }

    return { daysUntil, daysTotal, percentageComplete };
  };

  const filterPrograms = () => {
    let filtered = [...programs];

    if (categoryFilter !== 'all') {
      filtered = filtered.filter(p => p.category === categoryFilter);
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(p => {
        const { status } = getProgramStatus(p);
        return status === statusFilter;
      });
    }

    if (searchTerm) {
      filtered = filtered.filter(p =>
        p.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    setFilteredPrograms(filtered);
  };

  const formatCardDate = (date: any): string => {
    const parsedDate = parseDate(date);
    if (!parsedDate) return 'TBA';
    
    return parsedDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (date: any): string => {
    const parsedDate = parseDate(date);
    if (!parsedDate) return '';
    
    return parsedDate.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const categories = Array.from(new Set(programs.map(p => p.category).filter(Boolean))) as string[];
  const statuses = ['all', 'upcoming', 'ongoing', 'completed', 'published', 'cancelled'];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Hero Header */}
      <div className="relative bg-gradient-to-br from-slate-900 via-indigo-950 to-purple-900 pt-6 pb-8">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 backdrop-blur-sm text-cyan-300 rounded-full text-sm font-semibold mb-4">
              <CalendarDays className="text-xs" />
              Programs
            </div>

            <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
              Our Programs
            </h1>

            <p className="text-base md:text-lg text-gray-300 mb-4">
              Join our diverse range of programs designed to educate, empower, and inspire
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

      {/* Modern Filters and Search */}
      <div className="mb-10 bg-white p-6 rounded-2xl shadow-lg border border-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
          {/* Search */}
          <div className="md:col-span-5">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search programs
            </label>
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Search by title, description, or tags..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 transition-all duration-200"
              />
            </div>
          </div>

          {/* Category Filter */}
          <div className="md:col-span-3">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category
            </label>
            <div className="relative">
              <Filter className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 appearance-none transition-all duration-200"
              >
                <option value="all">All Categories</option>
                {categories.map(category => (
                  <option key={category} value={category}>
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Status Filter */}
          <div className="md:col-span-3">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 transition-all duration-200"
            >
              <option value="all">All Status</option>
              <option value="upcoming">Upcoming</option>
              <option value="ongoing">Ongoing</option>
              <option value="completed">Completed</option>
              <option value="published">Published</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          {/* Reset Button */}
          <div className="md:col-span-1">
            <button
              onClick={() => {
                setCategoryFilter('all');
                setStatusFilter('all');
                setSearchTerm('');
              }}
              className="w-full px-4 py-3 text-gray-700 border border-gray-300 rounded-xl hover:bg-gray-50 transition-all duration-200 font-medium hover:shadow-sm"
            >
              Reset
            </button>
          </div>
        </div>
         {/* Stats Summary */}
      {!loading && filteredPrograms.length > 0 && (
        <div className="mt-12 grid gap-6" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
          <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100">
            <div className="flex items-center">
              <Calendar className="h-8 w-8 text-blue-500 mr-4" />
              <div>
                <div className="text-2xl font-bold text-gray-900">
                  {filteredPrograms.filter(p => getProgramStatus(p).status === 'upcoming').length}
                </div>
                <div className="text-sm text-gray-600">Upcoming Programs</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100">
            <div className="flex items-center">
              <Target className="h-8 w-8 text-green-500 mr-4" />
              <div>
                <div className="text-2xl font-bold text-gray-900">
                  {filteredPrograms.filter(p => getProgramStatus(p).status === 'ongoing').length}
                </div>
                <div className="text-sm text-gray-600">Ongoing Programs</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100">
            <div className="flex items-center">
              <Award className="h-8 w-8 text-gray-500 mr-4" />
              <div>
                <div className="text-2xl font-bold text-gray-900">
                  {filteredPrograms.filter(p => getProgramStatus(p).status === 'completed').length}
                </div>
                <div className="text-sm text-gray-600">Completed</div>
              </div>
            </div>
          </div>
          
     
        </div>
      )}

      </div>


      {/* Modern Programs Grid */}
      {loading ? (
        <div className="flex justify-center items-center h-96">
          <div className="flex flex-col items-center space-y-4">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary-600 border-t-transparent"></div>
            <p className="text-gray-600">Loading programs...</p>
          </div>
        </div>
      ) : filteredPrograms.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 rounded-2xl border border-gray-200">
          <div className="max-w-md mx-auto">
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gray-100 flex items-center justify-center">
              <Search className="h-12 w-12 text-gray-400" />
            </div>
            <h3 className="text-2xl font-semibold text-gray-800 mb-3">No Programs Found</h3>
            <p className="text-gray-600 mb-6">Try adjusting your search or filters to find what you're looking for</p>
            <button
              onClick={() => {
                setCategoryFilter('all');
                setStatusFilter('all');
                setSearchTerm('');
              }}
              className="px-6 py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors font-medium"
            >
              Clear All Filters
            </button>
          </div>
        </div>
      ) : (
         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPrograms.map((program) => {
            const programStatus = getProgramStatus(program);
            const dateInfo = getDateInfo(program);
            
            return (
              <div 
                key={program.id} 
                className="group bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-primary-100 hover:-translate-y-1"
              >
                {/* Image with gradient overlay */}
                <div className="relative h-56 overflow-hidden">
                  {program.imageUrl ? (
                    <>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent z-10"></div>
                      <img 
                        src={program.imageUrl} 
                        alt={program.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </>
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center">
                      <div className="text-white text-center p-6">
                        <Users className="h-12 w-12 mx-auto mb-3 opacity-80" />
                        <p className="text-lg font-semibold">{program.title}</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Status Badge with date info */}
                  <div className="absolute top-4 right-4 z-20 flex flex-col gap-2">
                    <span className={`px-4 py-2 rounded-full text-xs font-semibold backdrop-blur-sm ${programStatus.bgColor} ${programStatus.color}`}>
                      {programStatus.label}
                    </span>
                    
                    {/* Date indicator */}
                    {dateInfo.daysUntil !== null && dateInfo.daysUntil > 0 && (
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white/90 text-purple-700 backdrop-blur-sm">
                        Starts in {dateInfo.daysUntil} day{dateInfo.daysUntil !== 1 ? 's' : ''}
                      </span>
                    )}
                    
                    {dateInfo.percentageComplete !== null && dateInfo.percentageComplete > 0 && (
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white/90 text-green-700 backdrop-blur-sm">
                        {dateInfo.percentageComplete}% Complete
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Card Content */}
                <div className="p-6">
                  {/* Category Tag */}
                  {program.category && (
                    <div className="mb-3">
                      <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-medium">
                        {program.category}
                      </span>
                    </div>
                  )}

                  {/* Title and Description */}
                  <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-1 group-hover:text-primary-600 transition-colors">
                    {program.title}
                  </h3>
                  
                  <p className="text-gray-600 mb-6 line-clamp-2 leading-relaxed">
                    {program.description}
                  </p>
                  
                  {/* Program Details */}
                  <div className="space-y-4 mb-6">
                    {/* Date & Time */}
                    <div className="space-y-2">
                      <div className="flex items-center text-gray-600">
                        <CalendarDays className="h-5 w-5 text-primary-500 mr-3 flex-shrink-0" />
                        <div className="ml-3 flex-1">
                          <div className="text-sm text-gray-500 flex items-center justify-between">
                            <span>Start Date</span>
                            <span className="font-medium">
                              {formatCardDate(program.startDate)}
                              {program.startTime && (
                                <span className="text-sm text-gray-500 ml-2">at {program.startTime}</span>
                              )}
                            </span>
                          </div>
                        </div>
                      </div>

                      {program.endDate && (
                        <div className="flex items-center text-gray-600">
                          <CheckCircle className="h-5 w-5 text-primary-500 mr-3 flex-shrink-0" />
                          <div className="ml-3 flex-1">
                            <div className="text-sm text-gray-500 flex items-center justify-between">
                              <span>End Date</span>
                              <span className="font-medium">
                                {formatCardDate(program.endDate)}
                                {program.endTime && (
                                  <span className="text-sm text-gray-500 ml-2">at {program.endTime}</span>
                                )}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Duration */}
                    {dateInfo.daysTotal !== null && (
                      <div className="flex items-center text-gray-600">
                        <Clock className="h-5 w-5 text-primary-500 mr-3 flex-shrink-0" />
                        <div className="ml-3 flex-1">
                          <div className="text-sm text-gray-500 flex items-center justify-between">
                            <span>Duration</span>
                            <span className="font-medium">{dateInfo.daysTotal} day{dateInfo.daysTotal !== 1 ? 's' : ''}</span>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Location */}
                    {program.location && (
                      <div className="flex items-center text-gray-600">
                        <MapPin className="h-5 w-5 text-primary-500 mr-3 flex-shrink-0" />
                        <div className="ml-3 flex-1">
                        <div className="flex items-center justify-between text-gray-500">
                        <span className="text-sm">Location</span>
                        <span className="text-xs font-medium text-gray-5  00">
                         {program.location}
                        </span>
                        </div>

                        </div>
                      </div>
                    )}
                    
                   
                    
                    {/* Progress Bar for ongoing programs */}
                    {dateInfo.percentageComplete !== null && dateInfo.percentageComplete > 0 && (
                      <div className="pt-2">
                        <div className="flex justify-between text-xs text-gray-500 mb-1">
                          <span>Progress</span>
                          <span>{dateInfo.percentageComplete}%</span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-green-500 rounded-full transition-all duration-500"
                            style={{ width: `${dateInfo.percentageComplete}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <Link 
                      to={`/programs/${program.id}`}
                      className="flex items-center text-primary-600 hover:text-primary-700 font-medium group/link"
                    >
                      View Details
                      <ArrowRight className="ml-2 h-4 w-4 group-hover/link:translate-x-1 transition-transform" />
                    </Link>
                    
                      {program.requiresRegistration && 
                       programStatus.status !== 'completed' && 
                       programStatus.status !== 'cancelled' &&
                       (!program.registrationEnd || 
                        !parseDate(program.registrationEnd) || 
                        now <= parseDate(program.registrationEnd)!) && (
                      <button
                        onClick={() => handleRegisterClick(program.id, program.title)}
                        className={`px-5 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl hover:from-primary-700 hover:to-primary-800 transition-all duration-200 font-medium shadow-sm hover:shadow-md ${
                          programStatus.status === 'upcoming' ? 'animate-pulse-subtle' : ''
                        }`}
                      >
                        {programStatus.status === 'upcoming' ? 'Register Now' : 'Register'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

     
      </div>

      <RequireLoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        programId={selectedProgram?.id}
        programTitle={selectedProgram?.title}
      />
    </div>
  );
};

export default Programs;
